import { logExceptionIntoSentry } from '../../error-logging';

export const loadScript = ({ id, src }: { id: string; src: string }): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.type = 'text/javascript';
    script.async = true;
    script.src = src;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));

    document.head.appendChild(script);
  });

export interface DeferredLoader {
  start: (load: () => Promise<void>) => void;
  /** Resolves once the action has run, or immediately when the provider was never loaded. */
  enqueue: (action: () => void) => Promise<void>;
}

/**
 * Providers are loaded only once functional cookies are accepted, so every call before that has to be
 * dropped rather than queued: a user who never accepts must not have their activity replayed later.
 */
export const createDeferredLoader = ({ name }: { name: string }): DeferredLoader => {
  let ready: Promise<void> | undefined;
  let hasReportedFailure = false;

  /** Provider work runs inside a promise, so a provider broken for one call is broken for every call. */
  const reportOnce = (error: Error) => {
    if (!hasReportedFailure) {
      hasReportedFailure = true;
      logExceptionIntoSentry(error, { analyticsProvider: name });
    }
  };

  return {
    start: (load: () => Promise<void>) => {
      if (ready !== undefined) {
        return;
      }
      ready = load().catch(reportOnce);
    },
    enqueue: (action: () => void) => ready?.then(action).catch(reportOnce) ?? Promise.resolve(),
  };
};
